import React from 'react';
import { motion } from 'framer-motion';
import { BRAND_BLUE } from '../../config/theme';
import RotatingTagline from '../RotatingTagline';

interface HeroSectionProps {
  logoSrc: string;
  currentLang: 'en' | 'fr' | 'de' | 'it';
}

/**
 * HeroSection Component
 * Displays the TTStats logo, animated title, rotating tagline, and animated stats
 */
const HeroSection: React.FC<HeroSectionProps> = ({ logoSrc, currentLang }) => {
  // SEO H1 text based on language
  const getSEOTitle = () => {
    switch (currentLang) {
      case 'de':
        return 'Tischtennis ELO Schweiz - TTStats';
      case 'fr':
        return 'Tennis de table Suisse ELO - TTStats';
      case 'it':
        return 'Ping Pong Svizzera ELO - TTStats';
      default:
        return 'Swiss Table Tennis ELO Rankings - TTStats';
    }
  };

  return (
    <div className="w-full text-center">
      {/* SEO H1 - Hidden but important for search engines */}
      <h1 className="sr-only">{getSEOTitle()}</h1>

      {/* Logo - appears first, then moves up slightly earlier */}
      <motion.img
        src={logoSrc}
        alt="TTStats"
        className="h-28 w-auto mb-8 mx-auto"
        initial={{ opacity: 0, y: 150, scale: 1.3 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          opacity: { duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] },
          y: { duration: 1.0, delay: 0.3, ease: [0.4, 0, 0.2, 1] },
          scale: { duration: 1.0, delay: 0.3, ease: [0.4, 0, 0.2, 1] }
        }}
      />

      {/* Title - moves up slightly after logo */}
      <motion.div
        initial={{ scale: 1.3, y: 150 }}
        animate={{ scale: 1, y: 0 }}
        transition={{
          duration: 1.2,
          delay: 0.5,
          ease: [0.4, 0, 0.2, 1]
        }}
      >

        {/* Title */}
        <div className="mb-3">
        <div className="text-5xl font-bold">
          {['T', 'T', 'S', 't', 'a', 't', 's', '.', 'c', 'h'].map((char, index) => (
            <motion.span
              key={index}
              className="inline-block transition-colors duration-300"
              style={{ color: index < 2 ? BRAND_BLUE : undefined }}
              data-dark-text={index >= 2 ? 'true' : undefined}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.2 + index * 0.03,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <span className={index < 2 ? '' : 'text-gray-900 dark:text-gray-100 transition-colors duration-300'}>
                {char}
              </span>
            </motion.span>
          ))}
        </div>
        </div>
      </motion.div>

      {/* Rotating Tagline - appears after scale completes */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1.5 }}
      >
        <RotatingTagline lang={currentLang} interval={4000} />
      </motion.div>
    </div>
  );
};

export default HeroSection;
